import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import { KycSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/styles.ts';
import { ENTITY_TABS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { Drawer, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';
import { useKycWorkflowCustomFields } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycWorkflowCustomFields.ts';
import { useInvestmentWorkflowInvestorData } from './useInvestmentWorkflowInvestorData.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	kycWorkflowId: string | undefined;
	initialActiveTab?: ENTITY_TABS;
}

export const InvestmentWorkflowInvestorDataDrawer = ({
	opened,
	onClose,
	kycWorkflowId,
	initialActiveTab,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [activeTab, setActiveTab] = useState<ENTITY_TABS>(ENTITY_TABS.INDIVIDUAL);
	const {
		individualSettings,
		vehicleSettings,
		toggleIndividualSetting,
		toggleVehicleSetting,
		isLoading,
	} = useInvestmentWorkflowInvestorData(kycWorkflowId);
	const { individualCustomFields, vehicleCustomFields, toggleCustomField } =
		useKycWorkflowCustomFields(kycWorkflowId);

	useEffect(() => {
		if (initialActiveTab && opened) {
			setActiveTab(initialActiveTab);
		}
	}, [initialActiveTab, opened]);

	const tabsConfig: MantineTabProps<ENTITY_TABS>[] = [
		{
			label: tMipManage('kyc_settings.label.individual'),
			value: ENTITY_TABS.INDIVIDUAL,
			icon: 'information',
		},
		{
			label: tMipManage('kyc_settings.label.vehicle'),
			value: ENTITY_TABS.INVESTMENT_VEHICLE,
			icon: 'derived_commuted_structure',
		},
	];

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('investments.drawer.investor_data.title')}
		>
			{isLoading && <InlineLoader />}

			<MantineTabs
				tabs={tabsConfig}
				panels={[]}
				onlyDividerBorder={true}
				value={activeTab}
				onTabChange={(value) => setActiveTab(value as ENTITY_TABS)}
			>
				{activeTab === ENTITY_TABS.INDIVIDUAL ? (
					<KycSection $noMaxWidth>
						<ToggleRequirementItem
							title={tMipManage('investor_data.bank_account.label')}
							description={tMipManage('investor_data.bank_account.description')}
							checked={individualSettings.bankAccountDetails.checked}
							disabled={individualSettings.bankAccountDetails.disabled}
							onToggle={() => toggleIndividualSetting('bankAccountDetails')}
						/>
						<ToggleRequirementItem
							title={tMipManage('investor_data.tax_residence.label')}
							description={tMipManage('investor_data.tax_residence.description')}
							checked={individualSettings.taxResidence.checked}
							disabled={individualSettings.taxResidence.disabled}
							onToggle={() => toggleIndividualSetting('taxResidence')}
						/>
						<ToggleRequirementItem
							title={tMipManage('investor_data.tin.label')}
							description={tMipManage('investor_data.tin.description')}
							checked={individualSettings.taxIdentificationNumber.checked}
							disabled={individualSettings.taxIdentificationNumber.disabled}
							onToggle={() => toggleIndividualSetting('taxIdentificationNumber')}
						/>
						<ToggleRequirementItem
							title={tMipManage('investor_data.place_of_birth.label')}
							description={tMipManage('investor_data.place_of_birth.description')}
							checked={individualSettings.placeOfBirth.checked}
							disabled={individualSettings.placeOfBirth.disabled}
							onToggle={() => toggleIndividualSetting('placeOfBirth')}
						/>
						<ToggleRequirementItem
							title={tMipManage('investor_data.country_of_birth.label', {
								defaultValue: 'Country of birth',
							})}
							description={tMipManage('investor_data.country_of_birth.description', {
								defaultValue: 'Require investors to provide their country of birth',
							})}
							checked={individualSettings.countryOfBirth.checked}
							disabled={individualSettings.countryOfBirth.disabled}
							onToggle={() => toggleIndividualSetting('countryOfBirth')}
						/>
						<ToggleRequirementItem
							title={tMipManage('investor_data.employee_id.label')}
							description={tMipManage('investor_data.employee_id.description')}
							checked={individualSettings.employeeId.checked}
							disabled={individualSettings.employeeId.disabled}
							onToggle={() => toggleIndividualSetting('employeeId')}
						/>
						<ToggleRequirementItem
							title={tMipManage('investor_data.citizenship.label')}
							description={tMipManage('investor_data.citizenship.description')}
							checked={individualSettings.citizenship.checked}
							disabled={individualSettings.citizenship.disabled}
							onToggle={() => toggleIndividualSetting('citizenship')}
							showBorder={individualCustomFields.length > 0}
						/>
						{individualCustomFields.length > 0 && (
							<>
								<Text fw={600} mt="sm">
									{tMipManage('investor_data.custom_fields.section_title')}
								</Text>
								{individualCustomFields.map((field, index) => (
									<ToggleRequirementItem
										key={field.customDetailId}
										title={field.name}
										description={field.instruction ?? ''}
										checked={field.isRequired}
										onToggle={() =>
											toggleCustomField(
												field.customDetailId,
												field.isRequired
											)
										}
										showBorder={index < individualCustomFields.length - 1}
									/>
								))}
							</>
						)}
					</KycSection>
				) : (
					<KycSection $noMaxWidth>
						<ToggleRequirementItem
							title={tMipManage('investor_data.bank_account.label')}
							description={tMipManage('investor_data.bank_account.description')}
							checked={vehicleSettings.bankAccountDetails.checked}
							disabled={vehicleSettings.bankAccountDetails.disabled}
							onToggle={() => toggleVehicleSetting('bankAccountDetails')}
						/>
						<ToggleRequirementItem
							title={tMipManage('investor_data.controlling_party.label')}
							description={tMipManage('investor_data.controlling_party.description')}
							checked={vehicleSettings.controllingPartyInfo.checked}
							disabled={vehicleSettings.controllingPartyInfo.disabled}
							onToggle={() => toggleVehicleSetting('controllingPartyInfo')}
							showBorder={vehicleCustomFields.length > 0}
						/>
						{vehicleCustomFields.length > 0 && (
							<>
								<Text fw={600} mt="sm">
									{tMipManage('investor_data.custom_fields.section_title')}
								</Text>
								{vehicleCustomFields.map((field, index) => (
									<ToggleRequirementItem
										key={field.customDetailId}
										title={field.name}
										description={field.instruction ?? ''}
										checked={field.isRequired}
										onToggle={() =>
											toggleCustomField(
												field.customDetailId,
												field.isRequired
											)
										}
										showBorder={index < vehicleCustomFields.length - 1}
									/>
								))}
							</>
						)}
					</KycSection>
				)}
			</MantineTabs>
		</Drawer>
	);
};
